import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcDtccUtcV1UtcFixSessionQueryServiceUtcFixSessionGet } from '@interxfi/api-client/generated/hooks/exc-dtcc-utc-v1-utc-fix-session-query-service-utc-fix-session-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import ClearIcon from '@interxfi/design-system/icons/react/icon-clear';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import PowerIcon from '@interxfi/design-system/icons/react/icon-power';
import UndoIcon from '@interxfi/design-system/icons/react/icon-undo';
import { type ExcDtccUtcV1UtcFixSessionState, ExcDtccUtcV1UtcFixSessionStatus } from '@interxfi/types';
import { UtcFixSessionSummaryBar } from '../../../../../../../../../../features/dtcc/utc-fix-session/summary-bar/utc-fix-session-summary-bar';
import { UtcFixSessionConnectDialog } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-connect-dialog';
import { UtcFixSessionDisconnectDialog } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-disconnect-dialog';
import { UtcFixSessionEditDrawer } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-edit-drawer';
import {
  UtcFixSessionEventDrawer,
  UtcFixSessionEventSummary,
} from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-event';
import { getUtcFixSessionStateListItems } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-list-items';
import { getUtcFixSessionDetailPath } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-routing';
import { UtcFixSessionSequenceResetDialog } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-sequence-reset-dialog';
import { UtcFixSessionStatusPill } from '../../../../../../../../../../features/dtcc/utc-fix-session/utc-fix-session-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/trade/utc-fix-session/_utc-fix-session/$sessionId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `utc-fix-session-${params.sessionId}`,
        label: 'UTC FIX Session',
        to: getUtcFixSessionDetailPath(params.sessionId),
        suffix: (
          <CopyButton
            content={params.sessionId}
            label="Copy UTC FIX Session ID"
            tooltip={{ copy: params.sessionId, copied: 'Copied UTC FIX Session ID' }}
          />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('UTC FIX Session'),
  component: UtcFixSessionDetail,
});

function getListItems(data: ExcDtccUtcV1UtcFixSessionState | undefined) {
  const listItems = getUtcFixSessionStateListItems(data);

  return {
    generalLeft: [
      listItems.statusPill,
      listItems.data.senderSeq,
      listItems.data.targetSeq,
      listItems.data.initiatorRunning,
      listItems.data.isLoggedOn,
    ] satisfies ReadonlyListItem[],
    generalRight: [listItems.sessionId, listItems.senderCompId, listItems.targetCompId] satisfies ReadonlyListItem[],
    config: [
      listItems.data.config.targetHost,
      listItems.data.config.targetPort,
      listItems.data.config.heartbeatInterval,
    ] satisfies ReadonlyListItem[],
  };
}

function getMenuItems(sessionId: string, status: ExcDtccUtcV1UtcFixSessionStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: UtcFixSessionEventDrawer,
        props: { request: { sessionId } },
      },
    },
    actions: [
      ...(status &&
      [
        ExcDtccUtcV1UtcFixSessionStatus.LoggedOn,
        ExcDtccUtcV1UtcFixSessionStatus.Connecting,
        ExcDtccUtcV1UtcFixSessionStatus.Disconnected,
        ExcDtccUtcV1UtcFixSessionStatus.Stopped,
      ].includes(status)
        ? [
            {
              id: 'edit',
              label: 'Edit FIX Session',
              icon: <EditIcon aria-hidden />,
              interaction: {
                type: 'drawer',
                drawer: {
                  Component: UtcFixSessionEditDrawer,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
            {
              id: 'resetSequence',
              label: 'Reset Sequence Numbers',
              icon: <UndoIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: UtcFixSessionSequenceResetDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status && [ExcDtccUtcV1UtcFixSessionStatus.Stopped, ExcDtccUtcV1UtcFixSessionStatus.Connecting].includes(status)
        ? [
            {
              id: 'start',
              label: 'Start FIX Session',
              icon: <PowerIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: UtcFixSessionConnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status &&
      [ExcDtccUtcV1UtcFixSessionStatus.LoggedOn, ExcDtccUtcV1UtcFixSessionStatus.Connecting, ExcDtccUtcV1UtcFixSessionStatus.Disconnected].includes(
        status,
      )
        ? [
            {
              id: 'stop',
              label: 'Stop FIX Session',
              tone: 'danger-dk',
              icon: <ClearIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: UtcFixSessionDisconnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
    ],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load UTC FIX session', 'view this UTC FIX session');

function UtcFixSessionDetail() {
  const { sessionId } = Route.useParams();
  const request = { sessionId } as const;
  const { data, error, isPending } = useExcDtccUtcV1UtcFixSessionQueryServiceUtcFixSessionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.utcFixSession), [data?.utcFixSession]);
  const menuItems = useMemo(() => getMenuItems(sessionId, data?.utcFixSession?.status), [sessionId, data?.utcFixSession?.status]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <UtcFixSessionStatusPill isPending={showPending} size="large" status={data?.utcFixSession?.status} />
            </HorizontalStack>
            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <UtcFixSessionSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <UtcFixSessionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.generalLeft} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.generalRight} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Config</Typography.Level3>

          <AppReadonlyList isPending={showPending} items={listItems.config} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
